// VQQ display-name formatting. Kept dependency-free (no hooks/api imports) so
// lightweight components and tests can use it without pulling the Clerk/axios
// chain that helperMethods.js carries.

// Cuts the trailing " - MMM YYYY - <location>" release/location suffix a VQQ name carries.
const VQQ_DATE_SUFFIX_REGEX =
  /\s-\s(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b.*$/i;

// Extracts just the template name from a full VQQ name for the Details page Template row.
// "Diligent - Diligent Signal Detection RFI - APR 2026 - DE, United States" -> "Diligent Signal Detection".
// Drops the leading "Diligent - " branding segment (keeping template names that themselves
// start with "Diligent"), the release/location suffix, and the " RFI" token.
export const getTemplateNameOnly = (name) => {
  if (!name || typeof name !== "string") return name ?? "";
  let result = name.trim();
  const segments = result.split(" - ");
  if (segments.length > 1 && segments[0].trim().toLowerCase() === "diligent") {
    result = segments.slice(1).join(" - ");
  }
  return result
    .replace(VQQ_DATE_SUFFIX_REGEX, "")
    .replace(/\s+RFI\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s*-\s*$/, "")
    .trim();
};

// Formats a full VQQ name for display: drops the leading "Diligent - " branding segment
// (keeping template names that themselves start with "Diligent") and the " RFI" token, while
// keeping the " - MMM YYYY - <location>" suffix. Mirrors NormalizeVqqName on the backend so
// the app and the export cover/header show the same name.
// "Diligent - Diligent Foundation - All - JUN 2026 - Rajasthan, India"
//   -> "Diligent Foundation - All - JUN 2026 - Rajasthan, India".
export const formatVqqName = (name) => {
  if (!name || typeof name !== "string") return name ?? "";
  let result = name.trim();
  const segments = result.split(" - ");
  if (segments.length > 1 && segments[0].trim().toLowerCase() === "diligent") {
    result = segments.slice(1).join(" - ");
  }
  return result
    .replace(/\s+RFI\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

// The locator a VQQ name carries in its last segment: "City, State".
// Empty unless BOTH parts are known, so an incomplete address never produces a half locator.
export const buildVqqLocationPart = (city, state) => {
  const trimmedCity = (city ?? "").trim();
  const trimmedState = (state ?? "").trim();
  return trimmedCity && trimmedState ? `${trimmedCity}, ${trimmedState}` : "";
};

// A trailing " - <A>, <B>" segment, i.e. the locator slot of "{template} - {date} - {location}".
// The comma is what makes this a locator: it leaves non-location trailing segments such as
// "Custom RFI" alone. Excluding the comma from the character classes keeps the two halves
// unambiguous, so the match stays linear instead of backtracking.
const VQQ_TRAILING_LOCATION_REGEX = /\s-\s[^-,]+(?:,[^-,]+)+$/;

// Drops the locator a name already carries so a fresh one can take its place.
export const stripVqqLocation = (name) => {
  if (!name || typeof name !== "string") return name ?? "";
  return name.replace(VQQ_TRAILING_LOCATION_REGEX, "").trim();
};

// Puts the vendor location on an already-formatted VQQ name as a " - City, State" suffix,
// replacing whatever locator the stored name carries rather than appending a second one.
// Names created before the locator moved to City, State carry " - <State>, <Country>", and
// those have to render the same way as new ones.
// "Diligent Foundation - All - JUL 2026" + {Farmingdale, NY}
//   -> "Diligent Foundation - All - JUL 2026 - Farmingdale, NY".
// "Diligent Foundation - Quality - AUG 2026 - MA, United States" + {Boston, MA}
//   -> "Diligent Foundation - Quality - AUG 2026 - Boston, MA".
export const appendVqqLocation = (formattedName, city, state) => {
  if (!formattedName || typeof formattedName !== "string") return formattedName ?? "";
  const locationPart = buildVqqLocationPart(city, state);
  if (!locationPart) return formattedName;
  return `${stripVqqLocation(formattedName)} - ${locationPart}`;
};

// Pulls "City, State" off a record, across the field casings the location, provider and
// document DTOs each use. Empty when either half is missing.
export const resolveVqqLocationPart = (item) => {
  if (!item) return "";
  const city =
    item.city ??
    item.locationCity ??
    item.location_city ??
    item.LocationCity ??
    item.provider_city ??
    item.document_city ??
    null;
  const state =
    item.state ??
    item.locationState ??
    item.location_state ??
    item.LocationState ??
    item.provider_state ??
    item.document_state ??
    null;
  return buildVqqLocationPart(city, state);
};

// Display name for a VQQ list/dashboard row: the formatted name with the vendor location
// as " - City, State", matching the Details-page subtitle. Falls back to the plain formatted
// name when the location is incomplete.
export const formatVqqRowNameWithLocation = (item) => {
  if (!item) return "";
  const city = item.locationCity ?? item.location_city ?? item.LocationCity ?? null;
  const state = item.locationState ?? item.location_state ?? item.LocationState ?? null;
  return appendVqqLocation(formatVqqName(item.name), city, state);
};
